import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import LandlordSidebar from '../../components/landlord/LandlordSidebar';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';
import PropertyManagersPanel from '../../components/shared/PropertyManagersPanel';
import DashboardSearch from '../../components/shared/DashboardSearch';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PIE_COLORS = ['#2563eb', '#f59e0b', '#dc2626', '#16a34a', '#7c3aed', '#0891b2'];

function ViewToggle({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} title={o.label} style={{
          padding: '4px 10px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
          border: 'none', background: value === o.value ? '#2563eb' : '#f1f5f9',
          color: value === o.value ? '#fff' : '#64748b', fontWeight: 600,
        }}>
          {o.icon}
        </button>
      ))}
    </div>
  );
}

const TREND_VIEWS = [
  { value: 'bar',    icon: '▬', label: 'Bar chart' },
  { value: 'line',   icon: '╱', label: 'Line chart' },
  { value: 'number', icon: '#', label: 'Number' },
];

const STATUS_VIEWS = [
  { value: 'pie',    icon: '◕', label: 'Pie chart' },
  { value: 'bar',    icon: '▬', label: 'Bar chart' },
  { value: 'number', icon: '#', label: 'Number' },
];

function NumberSummary({ items, valueKey = 'value', nameKey = 'name' }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
      {items.map((r, i) => (
        <div key={i} style={{ textAlign: 'center', minWidth: 80 }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: PIE_COLORS[i % PIE_COLORS.length] }}>{r[valueKey]}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{r[nameKey]}</div>
        </div>
      ))}
    </div>
  );
}

export default function LandlordDashboard() {
  const [data, setData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leftDataset, setLeftDataset] = useState('signups');
  const [signupView, setSignupView]   = useState('bar');
  const [revenueView, setRevenueView] = useState('line');
  const [statusView, setStatusView]   = useState('pie');
  const [checklist, setChecklist] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/landlord/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
    api.get('/landlord/dashboard/charts')
      .then(res => setCharts(res.data))
      .catch(() => {});
  }, []);

  // Load onboarding checklist for first property
  useEffect(() => {
    if (data?.properties?.[0]?.id) {
      api.get('/landlord/onboarding/' + data.properties[0].id)
        .then(res => setChecklist(res.data.steps))
        .catch(() => {});
    }
  }, [data]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const { totals, properties } = data || {};

  const leftData  = leftDataset === 'signups' ? charts?.signups_by_month : charts?.revenue_by_month;
  const leftKey   = leftDataset === 'signups' ? 'count' : 'total';
  const leftLabel = leftDataset === 'signups' ? 'Signups' : 'Revenue';
  const leftColor = leftDataset === 'signups' ? '#2563eb' : '#16a34a';
  const leftView  = leftDataset === 'signups' ? signupView : revenueView;
  const setLeftView = leftDataset === 'signups' ? setSignupView : setRevenueView;

  return (
    <div className="app-layout">
      <Topbar role="landlord" />
      <div className="dashboard-layout">
        <LandlordSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Portfolio Overview</div>
          <div className="page-sub">All properties · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>

          <DashboardSearch endpoint="/landlord/search" />

          {/* Stat cards */}
          <div className="metric-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
            <div className="metric-card"><div className="metric-label">Total units</div><div className="metric-value">{totals?.total_units || 0}</div></div>
            <div className="metric-card"><div className="metric-label">Active tenants</div><div className="metric-value accent">{totals?.active_tenants || 0}</div></div>
            <div className="metric-card"><div className="metric-label">Adoption rate</div><div className="metric-value green">{totals?.adoption_pct || 0}%</div></div>
            <div className="metric-card"><div className="metric-label">Revenue MTD</div><div className="metric-value sm">${fmt(totals?.revenue_mtd)}</div></div>
            <div className="metric-card"><div className="metric-label">Pending payout</div><div className="metric-value accent sm">${fmt(totals?.pending_payout)}</div></div>
          </div>

          {/* Onboarding Checklist */}
          {checklist && checklist.some(s => !s.completed) && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header"><div className="card-title">Onboarding Checklist</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                {checklist.map(step => (
                  <div key={step.key} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    {step.auto ? (
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: step.completed ? 'var(--green)' : 'var(--border)', color: 'white', fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>{step.completed ? '\u2713' : ''}</div>
                    ) : (
                      <input type="checkbox" checked={step.completed}
                        style={{ width: 20, height: 20, accentColor: 'var(--green)', cursor: 'pointer', flexShrink: 0 }}
                        onChange={async (e) => {
                          const newVal = e.target.checked;
                          setChecklist(prev => prev.map(s => s.key === step.key ? { ...s, completed: newVal } : s));
                          try {
                            await api.post('/landlord/onboarding/' + data.properties[0].id + '/toggle', { step_key: step.key, completed: newVal });
                          } catch { toast.error('Could not update checklist'); }
                        }} />
                    )}
                    <span style={{ fontSize: 14, color: step.completed ? 'var(--text-sub)' : 'var(--navy)', fontWeight: step.completed ? 400 : 600, textDecoration: step.completed ? 'line-through' : 'none' }}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts */}
          {charts && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

              {/* LEFT: Signups / Revenue */}
              <div className="card" style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[{ v: 'signups', label: 'Tenant Signups' }, { v: 'revenue', label: 'Revenue' }].map(o => (
                      <button key={o.v} onClick={() => setLeftDataset(o.v)} style={{
                        padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                        cursor: 'pointer', border: 'none',
                        background: leftDataset === o.v ? '#1e293b' : '#f1f5f9',
                        color: leftDataset === o.v ? '#fff' : '#64748b',
                      }}>{o.label}</button>
                    ))}
                  </div>
                  <ViewToggle options={TREND_VIEWS} value={leftView} onChange={setLeftView} />
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>Last 6 months</div>

                {leftView === 'number' ? (
                  <NumberSummary items={leftData || []} valueKey={leftKey} nameKey="month" />
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    {leftView === 'bar' ? (
                      <BarChart data={leftData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={leftDataset !== 'signups'} tickFormatter={leftDataset === 'revenue' ? v => '$' + v : undefined} />
                        <Tooltip formatter={leftDataset === 'revenue' ? v => '$' + parseFloat(v).toFixed(2) : undefined} />
                        <Bar dataKey={leftKey} name={leftLabel} fill={leftColor} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    ) : (
                      <LineChart data={leftData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={leftDataset === 'revenue' ? v => '$' + v : undefined} />
                        <Tooltip formatter={leftDataset === 'revenue' ? v => '$' + parseFloat(v).toFixed(2) : undefined} />
                        <Line type="monotone" dataKey={leftKey} name={leftLabel} stroke={leftColor} strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                )}
              </div>

              {/* RIGHT: Tenant Status */}
              <div className="card" style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div className="card-title" style={{ margin: 0 }}>Tenant Status</div>
                  <ViewToggle options={STATUS_VIEWS} value={statusView} onChange={setStatusView} />
                </div>

                {!charts.unit_status.length ? (
                  <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', paddingTop: 60 }}>No tenant data yet</div>
                ) : statusView === 'number' ? (
                  <NumberSummary items={charts.unit_status} />
                ) : statusView === 'pie' ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={charts.unit_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                        label={({ name, percent }) => name + ' ' + (percent * 100).toFixed(0) + '%'} labelLine={false} fontSize={12}>
                        {charts.unit_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={charts.unit_status} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" name="Tenants" radius={[4, 4, 0, 0]}>
                        {charts.unit_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

            </div>
          )}

          {/* Properties list */}
          <div className="card">
            <div className="card-header"><div className="card-title">My properties</div></div>
            <div>
              {(!properties || properties.length === 0) && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-sub)' }}>
                  No properties yet. Contact Affordable Telecom to add your first property.
                </div>
              )}
              {properties?.map((p, i) => (
                <div key={p.id} onClick={() => navigate('/landlord/properties/' + p.id)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: i < properties.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', transition: 'background 0.1s', flexWrap: 'wrap', gap: 12 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f7f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>{p.address} · {p.city}, {p.state}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}><div style={{ fontSize: 14, fontWeight: 700 }}>{p.unit_count}</div><div style={{ fontSize: 10, color: 'var(--text-sub)' }}>Units</div></div>
                    <div style={{ textAlign: 'center' }}><div style={{ fontSize: 14, fontWeight: 700 }}>{p.active_units}</div><div style={{ fontSize: 10, color: 'var(--text-sub)' }}>Active</div></div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: parseInt(p.adoption_pct) >= 70 ? 'var(--green)' : 'var(--accent)' }}>{p.adoption_pct}%</div>
                      <div style={{ fontSize: 10, color: 'var(--text-sub)' }}>Adoption</div>
                    </div>
                    <div style={{ textAlign: 'center' }}><div style={{ fontSize: 14, fontWeight: 700 }}>${fmt(p.revenue_mtd)}</div><div style={{ fontSize: 10, color: 'var(--text-sub)' }}>Rev MTD</div></div>
                    <span className="badge badge-green">Active</span>
                    <span style={{ color: 'var(--muted)', fontSize: 18 }}>›</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
